import React, {useState, useEffect, useRef, ChangeEvent} from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Breadcrumbs,
    Link,
    IconButton,
    Pagination,
    Stack,
    Chip,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {useTranslation} from 'react-i18next';
import ReactCrop, {Crop, PixelCrop, centerCrop, makeAspectCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {adminService} from "../../../api/services/admin";
import {getAccessToken} from "../../../utils/token";

// Интерфейс для производных изображений (деривативов)
interface ImageDerivative {
    path: string;
    width: number;
    height: number;
    size: number;
    format: string;
    createdAt: string;
}

// Интерфейс для деривативов изображения (объект с ключами)
interface ImageDerivatives {
    [key: string]: ImageDerivative | undefined;
}

// Интерфейс для файлов изображений
interface ImageFile {
    id: number | string;
    name: string;
    url: string;
    type: 'image';
    size: number; // Size in MB
    uploadDate: string; // Date of upload
    path?: string; // Путь к файлу
    processedFileId?: string; // ID обработанного файла
    derivatives?: ImageDerivatives; // Производные изображения как объект с ключами
}

export interface Folder {
    id: number | string;
    name: string;
    type: 'folder';
    children?: (Folder | ImageFile)[];
    childCount?: number;
}

interface ImageUploadProps {
    onSelectImage?: (image: ImageFile) => void;
}

export default function ImageUpload({ onSelectImage }: ImageUploadProps = {}) {
    const {t} = useTranslation();
    const [currentPath, setCurrentPath] = useState<string[]>([]);
    const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalElements, setTotalElements] = React.useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [folderData, setFolderData] = useState<Folder[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentItems, setCurrentItems] = useState<(Folder | ImageFile)[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);


    // Image upload and cropping states
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [imageType, setImageType] = useState<string>('thumbnail');
    const [maxSize, setMaxSize] = useState<number>(1);
    const imgRef = useRef<HTMLImageElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previewCanvasRef = useRef<HTMLCanvasElement>(null);

    // Загрузка корневых папок при монтировании компонента
    useEffect(() => {
        const fetchFoldersRoot = async () => {
            setLoading(true);
            try {
                const response = await adminService.getFoldersRoot(page, pageSize);
                console.log('API response:', response); // Отладочная информация

                if (response.status === 'success' && response.data) {
                    // Правильно обрабатываем структуру ответа API
                    console.log('Data from API:', response.data.data);

                    // Проверяем структуру данных и корректно обрабатываем вложенность
                    if (Array.isArray(response.data.data)) {
                        // Преобразуем данные в соответствии с нашим интерфейсом Folder
                        const foldersWithChildren = response.data.data.map((folder: Folder) => ({
                            ...folder,
                            // Добавляем пустой массив children, если его нет
                            children: folder.children || [],
                            // Добавляем childCount, если его нет
                            childCount: folder.childCount || 0
                        }));

                        console.log('Processed folders:', foldersWithChildren);
                        setFolderData(foldersWithChildren);

                        // Устанавливаем текущие элементы только если мы на главной странице
                        // и нет текущего пути, чтобы не перезаписать данные при навигации
                        if (currentPath.length === 0) {
                            console.log('Setting current items from API');
                            setCurrentItems(foldersWithChildren);
                        }
                    } else {
                        console.error('Unexpected data structure:', response.data.data);
                        setFolderData([]);
                        setCurrentItems([]);
                    }

                    // Обновляем информацию о пагинации
                    if (response.data.totalElements) {
                        setTotalElements(response.data.totalElements);
                        setTotalItems(response.data.totalElements);
                    }
                } else {
                    console.error('API response error:', response);
                    setFolderData([]);
                    setCurrentItems([]);
                }
            } catch (error) {
                console.error('Error fetching folders:', error);
                setFolderData([]);
                setCurrentItems([]);
            } finally {
                setLoading(false);
            }
        };

        fetchFoldersRoot();
    }, [page, pageSize, currentPath.length === 0]); // Загружаем корневые папки только когда находимся в корне

    // Get current folder based on path
    const getCurrentFolder = (): (Folder | ImageFile)[] => {
        console.log('getCurrentFolder called, folderData:', folderData);

        // Если нет данных, возвращаем пустой массив, чтобы избежать ошибок
        if (!folderData || folderData.length === 0) {
            console.log('No folder data available');
            return [];
        }

        // Если мы находимся в корневой директории, возвращаем корневые папки
        if (currentPath.length === 0) {
            return folderData;
        }

        let current: (Folder | ImageFile)[] = folderData;

        for (const pathSegment of currentPath) {
            const found = current.find(item => item.name === pathSegment);
            if (found && found.type === 'folder') {
                // Проверяем, существует ли массив children
                if (found.children) {
                    current = found.children;
                } else {
                    // Если у папки нет children, создаем пустой массив
                    console.log('No children in folder, creating empty array:', found);
                    found.children = [];
                    current = found.children;
                }
            } else {
                console.log('Path segment not found:', pathSegment);
                return [];
            }
        }

        console.log('Current folder contents:', current);
        return current;
    };

    // Функция для получения содержимого папки с пагинацией
    const fetchFolderContents = (
        folder: (Folder | ImageFile)[],
        pageNum: number,
        itemsPerPage: number
    ): Promise<{
        items: (Folder | ImageFile)[];
        totalPages: number;
        totalItems: number;
    }> => {
        return new Promise((resolve) => {
            // Небольшая задержка для имитации сетевого запроса
            const delay = Math.floor(Math.random() * 300) + 100;

            setTimeout(() => {
                const totalItems = folder.length;
                const indexOfLastItem = pageNum * itemsPerPage;
                const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                const paginatedItems = folder.slice(indexOfFirstItem, indexOfLastItem);
                const totalPages = Math.ceil(totalItems / itemsPerPage);

                // Обновляем общее количество элементов
                setTotalItems(totalItems);

                resolve({
                    items: paginatedItems,
                    totalPages: totalPages,
                    totalItems: totalItems
                });
            }, delay);
        });
    };

    // Handle folder click
    const handleFolderClick = async (folderName: string, folderId: number | string) => {
        setLoading(true);
        try {
            // Найдем ID папки, на которую кликнули
            const folder = currentItems.find(item => item.name === folderName && item.type === 'folder') as Folder | undefined;

            if (!folder) {
                console.error('Folder not found:', folderName);
                return;
            }

            console.log('Loading subfolders for folder:', folder);

            // Обновляем путь и сбрасываем состояние
            setCurrentPath([...currentPath, folderName]);
            setSelectedImage(null);
            setPage(0); // Сбросить на первую страницу (для бэкенда это 0)

            try {
                // Загружаем подпапки через API
                const response = await adminService.getSubFolders(folderId, page, pageSize);
                console.log('API response for subfolders:', response);

                if ((response.status === 'SUCCESS' || response.status === 'success') && response.data) {
                    console.log('Subfolders loaded:', response.data);

                    // Преобразуем данные в соответствии с нашим интерфейсом
                    if (Array.isArray(response.data.data)) {
                        const processedItems = response.data.data.map((item: any): Folder | ImageFile => {
                            if (item.type === 'folder') {
                                return {
                                    id: item.id,
                                    name: item.name,
                                    type: 'folder',
                                    children: [],
                                    childCount: item.folderCount || 0
                                } as Folder;
                            } else if (item.type === 'file') {
                                // Проверяем наличие изображения по расширению файла
                                const isImageFile = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(item.name);

                                // Преобразуем файлы в формат ImageFile
                                return {
                                    id: item.id,
                                    name: item.name,
                                    url: item.processedFileId ? `/api/files/${item.processedFileId}` : '', // Формируем URL для файла
                                    type: 'image',
                                    size: item.size ? Math.round((item.size / (1024 * 1024)) * 100) / 100 : 0, // Размер в МБ с округлением
                                    uploadDate: item.createdAt ? new Date(item.createdAt).toISOString().split('T')[0] : '',
                                    path: item.path || '',
                                    processedFileId: item.processedFileId,
                                    derivatives: item.derivatives,
                                } as ImageFile;
                            }
                            return item as unknown as Folder; // Fallback, не должно происходить
                        });

                        // Обновляем текущие элементы напрямую
                        setCurrentItems(processedItems);

                        // Обновляем информацию о пагинации
                        if (response.data.totalElements) {
                            setTotalElements(response.data.totalElements);
                            setTotalItems(response.data.totalElements);
                        }
                    }
                } else {
                    console.error('Failed to load subfolders:', response);
                    // Если API не вернуло данные, используем локальные
                    const currentFolder = getCurrentFolder();
                    setCurrentItems(currentFolder);
                    setTotalItems(currentFolder.length);
                }
            } catch (apiError) {
                console.error('API Error loading subfolders:', apiError);
                // В случае ошибки API, используем локальные данные как запасной вариант
                const currentFolder = getCurrentFolder();
                setCurrentItems(currentFolder);
                setTotalItems(currentFolder.length);
            }
        } catch (error) {
            console.error('Error loading subfolders:', error);
            setCurrentItems([]);
        } finally {
            setLoading(false);
        }
    };

// Handle image click
    const handleImageClick = async (image: ImageFile) => {
        // Set the image immediately to show basic info
        setSelectedImage(image);

        // Load the preview if we have a processedFileId
        if (image.processedFileId) {
            try {
                setPreviewLoading(true);
                const response = await adminService.loadImagePreview(image.id);

                if (response && response.status === 200 && response.data) {
                    // Создаем Blob из данных ответа
                    const blob = new Blob([response.data], {type: response.headers['content-type'] || 'image/jpeg'});
                    const imageUrl = URL.createObjectURL(blob);

                    // Update the image with the preview URL
                    setSelectedImage(prevImage => {
                        if (prevImage && prevImage.id === image.id) {
                            return {
                                ...prevImage,
                                url: imageUrl
                            };
                        }
                        return prevImage;
                    });
                } else {
                    console.error('Error loading image:', response?.status);
                }
            } catch (error) {
                console.error('Error loading image preview:', error);
            } finally {
                setPreviewLoading(false);
            }
        }
    };

    // Handle derivative click
    const handleDerivativeClick = async (derivativeType: string, derivative: ImageDerivative) => {
        if (selectedImage) {
            // Create a temporary image object with the derivative's path as URL initially
            const tempImage: ImageFile = {
                ...selectedImage,
                url: derivative.path, // Use path initially
                name: `${selectedImage.name} (${derivativeType})`,
                size: derivative.size || selectedImage.size
            };

            // Set the temporary image immediately to show something
            setSelectedImage(tempImage);
            try {
                // Load the preview if we have a processedFileId
                setPreviewLoading(true);
                const response = await adminService.loadImagePreview(selectedImage.id, derivativeType || null);
                if (response && response.status === 200 && response.data) {
                    // Создаем Blob из данных ответа
                    const blob = new Blob([response.data], {type: response.headers['content-type'] || 'image/jpeg'});
                    const imageUrl = URL.createObjectURL(blob);
                    // Update the image with the preview URL
                    setSelectedImage(prevImage => {
                        if (prevImage && prevImage.id === selectedImage.id) {
                            return {
                                ...prevImage,
                                url: imageUrl
                            };
                        }
                        return prevImage;
                    });
                } else {
                    console.error('Error loading image:', response?.status);
                }
            } catch (error) {
                console.error('Error loading image preview:', error);
            } finally {
                setPreviewLoading(false);
            }
        }
    };


// Handle back navigation
    const handleBack = async () => {
        if (currentPath.length > 0) {
            setLoading(true);
            try {
                const newPath = currentPath.slice(0, -1);
                setCurrentPath(newPath);
                setSelectedImage(null);
                setPage(0); // Сбросить на первую страницу (для бэкенда это 0)

                // Если возвращаемся в корень, загружаем корневые папки
                if (newPath.length === 0) {
                    const response = await adminService.getFoldersRoot(0, pageSize);
                    if (response.status === 'success' && response.data) {
                        // Обрабатываем данные как в основном методе загрузки
                        if (Array.isArray(response.data.data)) {
                            const foldersWithChildren = response.data.data.map((folder: Folder) => ({
                                ...folder,
                                children: folder.children || [],
                                childCount: folder.childCount || 0
                            }));
                            setCurrentItems(foldersWithChildren);

                            if (response.data.totalElements) {
                                setTotalElements(response.data.totalElements);
                                setTotalItems(response.data.totalElements);
                            }
                        }
                    }
                } else {
                    // Иначе находим ID родительской папки и загружаем её содержимое
                    // Здесь необходимо хранить историю папок с их ID, но для простоты пока оставим так
                    // В реальном приложении лучше хранить объекты с id и именем в path
                    console.log('TODO: Load parent folder content');
                }
            } catch (error) {
                console.error('Error during navigation back:', error);
            } finally {
                setLoading(false);
            }
        }
    };

// Handle breadcrumb navigation
    const handleBreadcrumbClick = async (index: number) => {
        setLoading(true);
        try {
            // Если кликнули на Home, загружаем корневые папки
            if (index === -1) {
                setCurrentPath([]);
                setSelectedImage(null);
                setPage(0);

                const response = await adminService.getFoldersRoot(0, pageSize);
                if (response.status === 'success' && response.data && Array.isArray(response.data.data)) {
                    const foldersWithChildren = response.data.data.map((folder: Folder) => ({
                        ...folder,
                        children: folder.children || [],
                        childCount: folder.childCount || 0
                    }));
                    setCurrentItems(foldersWithChildren);

                    if (response.data.totalElements) {
                        setTotalElements(response.data.totalElements);
                        setTotalItems(response.data.totalElements);
                    }
                }
            } else {
                const newPath = currentPath.slice(0, index + 1);
                setCurrentPath(newPath);
                setSelectedImage(null);
                setPage(0);

                // TODO: Здесь надо загрузить содержимое папки по ID
                // В реальном приложении нужно хранить историю посещенных папок с их ID
                console.log('TODO: Load folder content for path:', newPath);
            }
        } catch (error) {
            console.error('Error during breadcrumb navigation:', error);
        } finally {
            setLoading(false);
        }
    };

// Handle page change
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        // Just update the page state, the useEffect will handle fetching the data
        // Преобразуем страницу в формат бэкенда (начиная с 0)
        setPage(value - 1);
    };

// Handle like/unlike for a folder
    const handleLikeToggle = (folderId: number | string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent folder navigation when clicking like button

        // Create a deep copy of the folder data to modify
        const newFolderData = JSON.parse(JSON.stringify(folderData));

        // Helper function to find and update the folder
        const updateFolder = (items: (Folder | ImageFile)[]) => {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.type === 'folder') {
                    if (item.id === folderId) {
                        // Toggle isLiked and update likes count
                        return true;
                    }

                    // Recursively search in children if they exist
                    if ((item as Folder).children && (item as Folder).children!.length > 0 && updateFolder((item as Folder).children!)) {
                        return true;
                    }
                }
            }
            return false;
        };

        updateFolder(newFolderData);
        setFolderData(newFolderData);
    };

// Image upload and cropping functions
    const handleUploadClick = () => {
        // Reset states
        setUploadedImage(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
        setImageType('thumbnail');
        setMaxSize(1);

        // Open dialog
        setUploadDialogOpen(true);
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setUploadedImage(reader.result as string);
            });
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const {width, height} = e.currentTarget;

        // Initialize crop to center and 80% of the image
        const crop = centerCrop(
            makeAspectCrop(
                {
                    unit: '%',
                    width: 80,
                },
                1, // 1:1 aspect ratio
                width,
                height
            ),
            width,
            height
        );

        setCrop(crop);
    };

    const generateCroppedImage = () => {
        if (!imgRef.current || !completedCrop) return;

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        // Set canvas dimensions to cropped image size
        canvas.width = completedCrop.width;
        canvas.height = completedCrop.height;

        // Draw the cropped image
        ctx.drawImage(
            image,
            completedCrop.x,
            completedCrop.y,
            completedCrop.width,
            completedCrop.height,
            0,
            0,
            completedCrop.width,
            completedCrop.height
        );

        // Convert canvas to blob and create a new image
        canvas.toBlob((blob) => {
            if (!blob) return;

            // Here you would typically upload the blob to your server
            // For now, we'll just add it to the mock data

            const newImageId = `image-new-${Date.now()}`;
            const newImageName = `Uploaded Image ${Date.now()}.jpg`;
            const newImageUrl = URL.createObjectURL(blob);

            const newImage: ImageFile = {
                id: newImageId,
                name: newImageName,
                url: newImageUrl,
                type: 'image',
                size: blob.size / (1024 * 1024), // Convert bytes to MB
                uploadDate: new Date().toISOString().split('T')[0] // Today's date
            };

            // Add the new image to the current folder
            const newFolderData = JSON.parse(JSON.stringify(folderData));

            const addImageToFolder = (items: (Folder | ImageFile)[]) => {
                if (currentPath.length === 0) {
                    // If we're at the root, create a new folder for today
                    const today = new Date().toISOString().split('T')[0];
                    const existingFolder = items.find(item => item.type === 'folder' && item.name === today);

                    if (existingFolder && existingFolder.type === 'folder' && existingFolder.children) {
                        existingFolder.children.push(newImage);
                    } else {
                        const newFolder: Folder = {
                            id: `folder-new-${Date.now()}`,
                            name: today,
                            type: 'folder',
                            children: [newImage],
                            childCount: 0
                        };
                        items.unshift(newFolder);
                    }
                    return true;
                }

                // Navigate to the current folder
                let current: (Folder | ImageFile)[] = items;
                let currentPathIndex = 0;

                while (currentPathIndex < currentPath.length) {
                    const pathSegment = currentPath[currentPathIndex];
                    const found = current.find(item => item.name === pathSegment) as Folder | undefined;

                    if (found && found.type === 'folder') {
                        if (currentPathIndex === currentPath.length - 1) {
                            // We've reached the target folder, add the image
                            if (found.children) {
                                found.children.push(newImage);
                                return true;
                            } else {
                                // Создаем массив children, если его нет
                                found.children = [newImage];
                                return true;
                            }
                        }

                        current = found.children || [];
                        currentPathIndex++;
                    } else {
                        return false;
                    }
                }

                return false;
            };

            addImageToFolder(newFolderData);
            setFolderData(newFolderData);

            // Close the dialog
            setUploadDialogOpen(false);
        }, 'image/jpeg', 0.95); // 95% quality JPEG
    };

// Render breadcrumbs
    const renderBreadcrumbs = () => {
        return (
            <Breadcrumbs aria-label="breadcrumb" sx={{mb: 2}}>
                <Link
                    color="inherit"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        handleBreadcrumbClick(-1); // Используем -1 для индикации Home
                    }}
                >
                    {t('Home')}
                </Link>
                {currentPath.map((path, index) => (
                    <Link
                        key={index}
                        color={index === currentPath.length - 1 ? 'textPrimary' : 'inherit'}
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleBreadcrumbClick(index);
                        }}
                    >
                        {path}
                    </Link>
                ))}
            </Breadcrumbs>
        );
    };

// Render current folder contents
    const renderFolderContents = () => {
        const currentFolder = getCurrentFolder();
        console.log('renderFolderContents - currentItems:', currentItems);
        console.log('renderFolderContents - currentFolder:', currentFolder);
        const totalPages = Math.ceil(totalItems > 0 ? totalItems : currentFolder.length / pageSize);

        return (
            <>
                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                        <Typography variant="body1">{t('Loading...')}</Typography>
                    </Box>
                ) : (
                    <List dense sx={{width: '100%'}}>
                        {currentItems.map((item) => (
                            <ListItem
                                key={item.id}
                                onClick={() => {
                                    if (item.type === 'folder') {
                                        handleFolderClick(item.name, item.id);
                                    } else {
                                        handleImageClick(item);
                                    }
                                }}
                                sx={{
                                    bgcolor: selectedImage?.id === item.id ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                                    '&:hover': {
                                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                    cursor: 'pointer',
                                    py: 0.5
                                }}
                            >
                                <ListItemIcon>
                                    {item.type === 'folder' ? <FolderIcon color="primary"/> :
                                        <ImageIcon color="secondary"/>}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.name}
                                    secondary={
                                        item.type === 'folder' ? (
                                            <Box sx={{display: 'flex', alignItems: 'center', mt: 0.5}}>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={(e) => handleLikeToggle(item.id, e)}
                                                >
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Stack direction="column" spacing={1} sx={{mt: 0.5}}>
                                                <Stack direction="row" spacing={1}>
                                                    <Chip
                                                        label={`${(item as ImageFile).size} MB`}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                    />
                                                    <Chip
                                                        label={`Uploaded: ${(item as ImageFile).uploadDate}`}
                                                        size="small"
                                                        variant="outlined"
                                                        color="secondary"
                                                    />
                                                </Stack>

                                                {/* Show derivatives for selected image */}
                                                {selectedImage?.id === item.id && selectedImage.derivatives && Object.keys(selectedImage.derivatives).length > 0 && (
                                                    <Box sx={{mt: 1}}>
                                                        <Typography variant="caption" sx={{display: 'block', mb: 0.5}}>
                                                            {t('Derivatives')}
                                                        </Typography>
                                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                            {Object.entries(selectedImage.derivatives).map(([type, derivative]) => (
                                                                derivative && (
                                                                    <Chip
                                                                        key={type}
                                                                        label={type}
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation(); // Prevent triggering the ListItem click
                                                                            handleDerivativeClick(type, derivative);
                                                                        }}
                                                                        color="info"
                                                                        variant="outlined"
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                            mb: 0.5
                                                                        }}
                                                                    />
                                                                )
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                )}
                                            </Stack>
                                        )
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

                {totalPages > 1 && (
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2}}>
                        <Typography variant="body2" color="textSecondary" sx={{mb: 1}}>
                            {t('Showing {{start}} to {{end}} of {{total}} items', {
                                start: (page * pageSize) + 1,
                                end: Math.min((page + 1) * pageSize, totalItems),
                                total: totalItems
                            })}
                        </Typography>
                        <Pagination
                            count={totalPages}
                            page={page + 1} /* Преобразуем страницу для компонента Pagination (начиная с 1) */
                            onChange={handlePageChange}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>
                )}
            </>
        );
    };

    return (
        <Box sx={{minHeight: '80vh', display: 'flex', flexDirection: 'column'}}>
            <Typography variant="h4" component="h1" gutterBottom>
                {t('Image Upload')}
            </Typography>
            <Paper sx={{p: 2, mb: 2, flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
                <Box sx={{display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between'}}>
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                        {currentPath.length > 0 && (
                            <IconButton onClick={handleBack} sx={{mr: 1}}>
                                <ArrowBackIcon/>
                            </IconButton>
                        )}
                        {renderBreadcrumbs()}
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CloudUploadIcon/>}
                        onClick={handleUploadClick}
                    >
                        {t('Upload Image')}
                    </Button>
                </Box>
                <Divider sx={{mb: 2}}/>
                <Grid container spacing={2} sx={{flexGrow: 1, minHeight: 0}}>
                    <Grid item xs={12} md={6} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: '500px',
                        overflow: 'auto'
                    }}>
                        <Typography variant="h6" gutterBottom>
                            {t('Files')}
                        </Typography>
                        <Box sx={{flexGrow: 1, overflow: 'auto'}}>
                            {renderFolderContents()}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}
                          sx={{display: 'flex', flexDirection: 'column', height: '100%', minHeight: '500px'}}>
                        <Typography variant="h6" gutterBottom>
                            {t('Preview')}
                        </Typography>
                        <Box sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                            {selectedImage ? (
                                <Box sx={{
                                    textAlign: 'center',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}>
                                    {previewLoading ? (
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: 'calc(100% - 120px)'
                                        }}>
                                            <Typography variant="body1">{t('Loading preview...')}</Typography>
                                        </Box>
                                    ) : (
                                        <img
                                            src={selectedImage.url}
                                            alt={selectedImage.name}
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: 'calc(100% - 120px)', // Оставляем место для названия, информации и деривативов
                                                objectFit: 'contain',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                padding: '4px'
                                            }}
                                        />
                                    )}
                                    <Typography variant="subtitle1" sx={{mt: 1}}>
                                        {selectedImage.name}
                                    </Typography>
                                    <Stack direction="row" spacing={2} justifyContent="center" sx={{mt: 1}}>
                                        <Chip
                                            label={`${selectedImage.size} MB`}
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`Uploaded: ${selectedImage.uploadDate}`}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                            {onSelectImage && (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => {
                                                        console.log('Отправка изображения в родительский компонент:', selectedImage);
                                                        onSelectImage(selectedImage);
                                                    }}
                                                >
                                                    {t('Выбрать это изображение')}
                                                </Button>
                                            )}
                                        {onSelectImage && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => onSelectImage(selectedImage)}
                                            >
                                                {t('Select this image')}
                                            </Button>
                                        )}
                                    </Stack>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        height: '100%',
                                        minHeight: '300px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                                        borderRadius: '4px'
                                    }}
                                >
                                    <Typography variant="body1" color="textSecondary">
                                        {t('Select an image to preview')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Image Upload Dialog */}
            <Dialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{t('Upload and Crop Image')}</DialogTitle>
                <DialogContent>
                    <Box sx={{mb: 2}}>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            style={{display: 'none'}}
                            ref={fileInputRef}
                        />
                        <Button
                            variant="contained"
                            onClick={() => fileInputRef.current?.click()}
                            startIcon={<CloudUploadIcon/>}
                            sx={{mb: 2}}
                        >
                            {t('Select Image')}
                        </Button>

                        <Grid container spacing={2} sx={{mt: 1}}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="image-type-label">{t('Image Type')}</InputLabel>
                                    <Select
                                        labelId="image-type-label"
                                        value={imageType}
                                        label={t('Image Type')}
                                        onChange={(e) => setImageType(e.target.value)}
                                    >
                                        <MenuItem value="thumbnail">{t('Thumbnail')}</MenuItem>
                                        <MenuItem value="banner">{t('Banner')}</MenuItem>
                                        <MenuItem value="profile">{t('Profile')}</MenuItem>
                                        <MenuItem value="gallery">{t('Gallery')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={t('Max Size (MB)')}
                                    type="number"
                                    value={maxSize}
                                    onChange={(e) => setMaxSize(parseFloat(e.target.value))}
                                    InputProps={{inputProps: {min: 0.1, step: 0.1}}}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {uploadedImage ? (
                        <Box sx={{mt: 2, textAlign: 'center'}}>
                            <ReactCrop
                                crop={crop}
                                onChange={(c) => setCrop(c)}
                                onComplete={(c) => setCompletedCrop(c)}
                                aspect={1}
                            >
                                <img
                                    ref={imgRef}
                                    src={uploadedImage}
                                    alt="Upload"
                                    style={{maxWidth: '100%'}}
                                    onLoad={onImageLoad}
                                />
                            </ReactCrop>
                            <Typography variant="body2" sx={{mt: 1, color: 'text.secondary'}}>
                                {t('Drag to crop the image. The crop maintains a 1:1 aspect ratio.')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                height: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                borderRadius: '4px'
                            }}
                        >
                            <Typography variant="body1" color="textSecondary">
                                {t('Select an image to crop')}
                            </Typography>
                        </Box>
                    )}

                    {completedCrop && (
                        <Box sx={{mt: 2, display: 'none'}}>
                            <canvas
                                ref={previewCanvasRef}
                                style={{
                                    border: '1px solid black',
                                    objectFit: 'contain',
                                    width: completedCrop.width,
                                    height: completedCrop.height,
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialogOpen(false)}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={generateCroppedImage}
                        variant="contained"
                        color="primary"
                        disabled={!completedCrop || !uploadedImage}
                    >
                        {t('Upload')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
